import styled, { CSSProperties } from 'styled-components';

import { BodilColor, BodilFont, NorlysColor, NorlysFont } from '~src/branding';
import { boxShadows } from '~src/branding/boxShadow';
import { useBranding } from '~src/context/BrandingContext';
import { calculateDisabledColor, calculateHoverColor } from '~src/util/color';

import { ButtonProps, ButtonSize, ButtonType } from './types';

const { base, ui, greyscale } = BodilColor;

type StyledButtonProps = Pick<ButtonProps, 'size' | 'disabled' | 'isLoading' | 'center'> & {
  buttonType?: ButtonType;
  isMobile?: boolean;
  colorContext?: ReturnType<typeof useBranding>;
  color?: string;
};

const commonStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 'fit-content',
  height: 'fit-content',
  borderRadius: '100px',
};

export const StyledButton = styled.button<StyledButtonProps>(
  ({ size = 'middle', disabled, isLoading, buttonType: type = 'default', center, isMobile, colorContext, color }) => {
    const disabledState = disabled || isLoading;

    const { lightColor, darkColor, darkTextColor, lightTextColor } = colorContext ?? {
      lightColor: ui.action.default,
      darkColor: base.default,
      darkTextColor: base.default,
      lightTextColor: base.white,
    };

    const hoverColorCTA = calculateHoverColor(color ?? lightColor);
    const hoverColorPlain = calculateHoverColor(color ?? lightTextColor);

    const disabledColorCTA = calculateDisabledColor(color ?? lightColor);
    const disabledColorPlain = calculateDisabledColor(color ?? lightTextColor);

    const fontSize = buttonPropertyBySize(14, 16, 20)(size);
    const hoverColor = buttonPropertyByType(hoverColorCTA, hoverColorPlain, greyscale[30])(type);

    const disabledColor = buttonPropertyByType(disabledColorCTA, disabledColorPlain, greyscale[40])(type);

    const fontColor = buttonPropertyByType(lightTextColor, darkTextColor, darkTextColor)(type);

    const background = buttonPropertyByType(color ?? lightColor, lightTextColor, greyscale[40])(type);

    const padding = buttonPropertyBySize('10px 16px', '16px 24px', '20px 32px')(size);

    const shadowStyle = isMobile ? boxShadows.mobileBodil : {};

    return {
      ...commonStyle,
      ...BodilFont.style('heading', fontSize as BodilFont.Size),
      ...shadowStyle,
      fontSize,
      lineHeight: `${fontSize}px`,

      padding,
      margin: `16px ${center ? 'auto' : '0'}`,

      cursor: disabledState ? 'default' : 'pointer',

      background: disabledState ? disabledColor : background,
      border: type === 'plain' ? `1px solid ${darkColor}` : 'none',
      borderRadius: '100px',
      color: disabledState && type !== 'callToAction' ? greyscale[10] : fontColor,

      ':hover': { background: disabledState ? undefined : hoverColor },
    };
  }
);

export const StyledButtonNorlys = styled.button<
  Pick<ButtonProps, 'size' | 'disabled' | 'isLoading' | 'center'> & { buttonType?: ButtonType }
>(({ size = 'middle', disabled, isLoading, buttonType: type = 'default', center }) => {
  const disabledState = disabled || isLoading;

  const fontSize = buttonPropertyBySize(14, 16, 20)(size);

  const disabledColor = greyscale[30];

  const background = buttonPropertyByType(
    NorlysColor.primary.acid,
    NorlysColor.secondary.darkGreen,
    NorlysColor.secondary.darkGreen
  )(type);

  const padding = buttonPropertyBySize('10px 16px', '16px 24px', '20px 32px')(size);

  return {
    ...commonStyle,
    ...NorlysFont.style('default', fontSize as NorlysFont.Size),
    textTransform: 'uppercase',
    fontSize,
    lineHeight: `${fontSize}px`,

    padding,
    margin: `16px ${center ? 'auto' : '0'}`,
    cursor: disabledState ? 'default' : 'pointer',

    background: disabledState ? disabledColor : background,
    border: 'none',
    color: 'white',
    fontWeight: 500,

    ':hover': { ...(disabledState ? {} : boxShadows.norlysButton) },
  };
});

export const IconContainer = styled.div({ padding: '0 8px' });

const buttonPropertyBySize =
  <T>(s: T, m: T, l: T) =>
  (size: ButtonSize) =>
    size === 'small' ? s : size === 'large' ? l : m;

export const buttonPropertyByType =
  <T>(cta: T, p: T, d: T) =>
  (type: ButtonType) =>
    type === 'callToAction' ? cta : type === 'plain' ? p : d;
